import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../AuthContext";
import { formatErr } from "../api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      // Always route to the role-based home; ignore `from` to avoid cross-role redirects.
      navigate(u.role === "admin" ? "/staff/admin" : "/staff/dashboard", {
        replace: true,
      });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="font-display text-ink block text-center mb-10"
          style={{ letterSpacing: "0.18em", fontSize: "1.25rem" }}
        >
          MATCHA<span className="text-matcha-dark">·</span>STORY
          <p className="overline text-ink-soft mt-3 tracking-[0.3em]">Staff Portal</p>
        </Link>

        <div className="bg-bone border border-ink/10 rounded-sm p-8 md:p-10">
          <h1 className="font-display text-2xl text-ink mb-1" style={{ letterSpacing: "0.06em" }}>
            Sign in
          </h1>
          <p className="text-sm text-ink-soft mb-8">
            Welcome back. Whisking begins.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline text-ink-soft block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-cream border border-ink/15 rounded-sm px-4 py-3 focus:outline-none focus:border-matcha-dark transition-colors"
              />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-cream border border-ink/15 rounded-sm pl-4 pr-11 py-3 focus:outline-none focus:border-matcha-dark transition-colors"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-soft hover:text-ink transition-colors"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && (
              <p data-testid="login-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
                {err}
              </p>
            )}

            <button
              data-testid="login-submit"
              disabled={busy}
              type="submit"
              className="w-full bg-ink text-bone rounded-full py-3 text-xs uppercase tracking-[0.22em] font-medium hover:bg-matcha-dark transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="text-center text-sm text-ink-soft mt-8">
            New here?{" "}
            <Link
              data-testid="link-to-register"
              to="/staff/register"
              className="text-matcha-dark hover:underline"
            >
              Register as staff
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
